import type { StoreSettings, WsEvent, WsHeartbeat } from '../types';

import { getOrCreateDeviceId, randomUuid } from './device-id';

/**
 * The gateway sends a `heartbeat` frame once a minute. If nothing at all
 * arrives for this long the connection is treated as lost even though the
 * browser may still report it as open (a half-open TCP connection after a
 * Wi-Fi blip is the classic case): the socket is dropped, `onStale` fires
 * and the normal reconnect loop takes over.
 *
 * Default only. The runtime passes `config.wsHeartbeatTimeoutMs`, sourced
 * from `VITE_WS_HEARTBEAT_TIMEOUT_MS` (see src/config.ts).
 */
export const HEARTBEAT_TIMEOUT_MS = 3 * 60_000;

/**
 * A connection must stay open this long before the reconnect backoff resets.
 * Without it a gateway that accepts the upgrade and closes straight away
 * (revoked key, overloaded node) would be hammered once a second forever.
 */
export const STABLE_CONNECTION_MS = 10_000;

export interface OrderSocketHandlers {
  onOpen: () => void;
  onClose: () => void;
  onEvent: (event: WsEvent) => void;
  onError: (err: unknown) => void;
  /** A gateway heartbeat arrived. Informational; the watchdog is internal. */
  onHeartbeat?: (event: WsHeartbeat) => void;
  /**
   * Nothing arrived from the gateway for `heartbeatTimeoutMs`. Fired after
   * the dead connection has been dropped and `onClose` reported. The runtime
   * uses it to re-initialise the whole live data layer.
   */
  onStale?: () => void;
}

export interface OrderSocketInput {
  settings: StoreSettings;
  xApiKey: string;
  handlers: OrderSocketHandlers;
  /** Silence tolerated before the connection is dropped. Defaults to HEARTBEAT_TIMEOUT_MS. */
  heartbeatTimeoutMs?: number;
}

/**
 * Browser-side WebSocket client for the Order Gateway. Per §2.7, the
 * browser authenticates via query parameters (custom headers aren't
 * available to the WebSocket constructor in the browser).
 *
 * On connect:
 *   - send a `subscribe` message with the store context
 *   - forward `order_created` and `order_status_updated` events
 *   - treat `heartbeat` frames (and any other inbound frame) as proof of life
 *   - silently drop everything else (including the literal "Subscribed"
 *     ack and any malformed JSON)
 *
 * Connection lifecycle:
 *   - `close` (the browser fires it after every `error` too) -> `onClose`,
 *     then reconnect with exponential backoff (1 s ... 30 s). The backoff
 *     only resets once a connection has stayed open for STABLE_CONNECTION_MS.
 *   - the heartbeat watchdog is armed from the moment a connection attempt
 *     starts, so a socket stuck in CONNECTING or gone silent after opening is
 *     dropped after `heartbeatTimeoutMs` and `onStale` fires.
 *   - a failed `subscribe` send drops the socket immediately: an open but
 *     unsubscribed connection would look healthy while delivering nothing.
 */
export class OrderSocket {
  private ws: WebSocket | null = null;
  private closed = false;
  private retry = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private watchdog: ReturnType<typeof setTimeout> | null = null;
  private stableTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly heartbeatTimeoutMs: number;

  constructor(private readonly input: OrderSocketInput) {
    this.heartbeatTimeoutMs = input.heartbeatTimeoutMs ?? HEARTBEAT_TIMEOUT_MS;
  }

  start(): void {
    this.closed = false;
    this.connect();
  }

  stop(): void {
    this.closed = true;
    this.disarmWatchdog();
    this.clearStableTimer();
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* swallow */
      }
      this.ws = null;
    }
  }

  private connect(): void {
    const { settings, xApiKey, handlers } = this.input;
    let url: URL;
    try {
      url = new URL(settings.wsUrl);
    } catch (err) {
      handlers.onError(err);
      this.scheduleReconnect();
      return;
    }
    url.searchParams.set('xApiKey', xApiKey);
    url.searchParams.set('storeId', String(settings.storeId));
    url.searchParams.set('deviceId', getOrCreateDeviceId());

    let ws: WebSocket;
    try {
      ws = new WebSocket(url.toString());
    } catch (err) {
      // Tizen / very old Chromium may throw here when WS support is partial.
      handlers.onError(err);
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    // Count from the attempt, not from `open`: a socket stuck in CONNECTING
    // must be abandoned too.
    this.armWatchdog();

    ws.onopen = () => {
      // Give the first heartbeat a full window from the moment we are open.
      this.armWatchdog();
      // Only a connection that survives for a while proves the backoff can reset.
      this.clearStableTimer();
      this.stableTimer = setTimeout(() => {
        this.stableTimer = null;
        this.retry = 0;
      }, STABLE_CONNECTION_MS);
      try {
        ws.send(
          JSON.stringify({
            action: 'subscribe',
            eventId: randomUuid(),
            timestamp: new Date().toISOString(),
            data: {
              storeId: settings.storeId,
              marketId: settings.marketId,
              marketName: settings.marketName,
              storeName: settings.storeName,
            },
          }),
        );
      } catch (err) {
        // Without a subscription the connection is useless. Drop it; the
        // browser's `close` event then feeds the normal reconnect loop.
        handlers.onError(err);
        try {
          ws.close();
        } catch {
          /* swallow */
        }
        return;
      }
      handlers.onOpen();
    };

    ws.onmessage = (msg: MessageEvent) => {
      // Any inbound frame proves the connection is alive. The heartbeat is the
      // one the gateway guarantees every minute; order events and the
      // "Subscribed" ack prove exactly the same thing when they happen to
      // arrive, so they count too.
      this.armWatchdog();
      const raw = msg.data;
      // The gateway sends a literal "Subscribed" string on successful sub.
      if (typeof raw !== 'string' || raw.charAt(0) !== '{') return;
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        return;
      }
      if (isHeartbeat(parsed)) {
        handlers.onHeartbeat?.(parsed);
        return;
      }
      if (!isWsEvent(parsed)) return;
      handlers.onEvent(parsed);
    };

    ws.onerror = (ev) => {
      handlers.onError(ev);
    };

    ws.onclose = () => {
      this.disarmWatchdog();
      this.clearStableTimer();
      this.ws = null;
      handlers.onClose();
      if (!this.closed) this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    this.retry = Math.min(this.retry + 1, 6);
    const delay = Math.min(30_000, 500 * 2 ** this.retry); // 1s, 2s, 4s, ... cap 30s
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, delay);
  }

  private armWatchdog(): void {
    this.disarmWatchdog();
    this.watchdog = setTimeout(() => this.handleStale(), this.heartbeatTimeoutMs);
  }

  private disarmWatchdog(): void {
    if (this.watchdog) {
      clearTimeout(this.watchdog);
      this.watchdog = null;
    }
  }

  private clearStableTimer(): void {
    if (this.stableTimer) {
      clearTimeout(this.stableTimer);
      this.stableTimer = null;
    }
  }

  /**
   * The gateway went silent. Drop the socket ourselves rather than wait for
   * the browser to notice (a half-open connection may never fire `close`),
   * report it as closed, let the owner re-initialise, then fall back to the
   * normal reconnect loop unless the owner stopped us in the meantime.
   */
  private handleStale(): void {
    this.watchdog = null;
    this.clearStableTimer();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      // Detach first so a late `close` from the dead socket cannot double-report
      // or schedule a second reconnect.
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      try {
        ws.close();
      } catch {
        /* swallow */
      }
    }
    const { handlers } = this.input;
    handlers.onClose();
    handlers.onStale?.();
    if (!this.closed) this.scheduleReconnect();
  }
}

function isHeartbeat(value: unknown): value is WsHeartbeat {
  return (
    !!value &&
    typeof value === 'object' &&
    (value as { action?: unknown }).action === 'heartbeat'
  );
}

function isWsEvent(value: unknown): value is WsEvent {
  if (!value || typeof value !== 'object') return false;
  const action = (value as { action?: unknown }).action;
  if (action !== 'order_created' && action !== 'order_status_updated') return false;
  const data = (value as { data?: unknown }).data;
  return !!data && typeof data === 'object';
}
